/**
 * 환경설정 &gt; 메일함 (2026-09-05 사용자 지시).
 *
 * 두 표다 — 위는 **메일함**(정본 **E1-2 계층 표**의 첫 사용), 아래는 **태그**.
 *
 * ★★ **시스템 메일함은 표에 서지만 고칠 수 없다.** 받은편지함·보낸편지함·임시보관함·
 *    내게쓴메일함은 `mail_folders` 에 행이 없다(코드 상수) → 보관기간을 걸 자리가 없어
 *    **늘 무제한**이고 **지울 수 없다**. 감추지 않고 잠근다(정본 P3).
 *    스팸함만 30일인데 그것은 서버 상수(`MAIL_SPAM_RETENTION_DAYS`)가 정한다.
 * ★★ **백업 · 공유 · 업로드는 잠겨 있다** — 서버에 길이 아직 없다(2026-09-05 사용자 결정).
 *    자리는 두고 왜 못 하는지 적는다. 이동 · 비우기 · 삭제는 지금 바로 된다.
 * ★  「안읽은」이 `null` 인 칸은 **뜻이 없다**는 말이다(보낸편지함·임시보관함) → 「—」로 적는다.
 *    0 으로 적으면 「다 읽었다」는 거짓이 된다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createMailFolder, deleteMailFolder, emptyFolder, getMailboxStats, listMailFolders,
  updateMailFolder, listMailTags, createMailTag, updateMailTag, deleteMailTag,
  type MailboxStat, type MailboxStats, type MailFolderNode, type MailSystemKey,
  type MailTag, type MailTagColor,
} from '../../api/mail';
import { Download, Edit3, FolderInput, MoreVertical, Plus, Share2, Trash2, Upload } from '../../icons';
import { Button } from '../../components/shared/Button';
import { Select } from '../../components/shared/Select';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailConfirmDialog } from './MailConfirmDialog';
import { MailFolderModal } from './MailFolderModal';
import { MailTagModal } from './MailTagModal';
import { notifyMailFoldersChanged, subscribeMailFolders } from './mailFolderBus';

/** 표에 서는 시스템 메일함 — 왼쪽 레일의 차례 그대로. */
const SYSTEM_ROWS: { key: MailSystemKey; labelKey: MailI18nKey; canEmpty: boolean }[] = [
  { key: 'inbox', labelKey: 'mail.folder.inbox', canEmpty: true },
  { key: 'star', labelKey: 'mail.folder.star', canEmpty: true },
  { key: 'sent', labelKey: 'mail.folder.sent', canEmpty: false },
  { key: 'draft', labelKey: 'mail.folder.draft', canEmpty: false },
  { key: 'self', labelKey: 'mail.folder.self', canEmpty: true },
  { key: 'spam', labelKey: 'mail.folder.spam', canEmpty: true },
  { key: 'trash', labelKey: 'mail.folder.trash', canEmpty: true },
];

/** 스팸함만 서버 상수로 30일이다 — 화면이 지어내지 않고 그 사실을 적는다. */
const SPAM_RETENTION_DAYS = 30;

/** 고를 수 있는 보관기간. `null` 이 무제한이다(0 을 쓰지 않는 이유는 migration 159 주석 참조). */
const RETENTION_OPTIONS: { value: string; days: number | null; labelKey: MailI18nKey }[] = [
  { value: 'none', days: null, labelKey: 'mail.mbox.unlimited' },
  { value: '30', days: 30, labelKey: 'mail.mbox.r.1m' },
  { value: '90', days: 90, labelKey: 'mail.mbox.r.3m' },
  { value: '180', days: 180, labelKey: 'mail.mbox.r.6m' },
  { value: '365', days: 365, labelKey: 'mail.mbox.r.1y' },
  { value: '1095', days: 1095, labelKey: 'mail.mbox.r.3y' },
  { value: '1825', days: 1825, labelKey: 'mail.mbox.r.5y' },
];

/** 목록에 없는 날수가 저장돼 있으면 **그 날수를 그대로 적는다** — 지어내지 않는다. */
function retentionLabel(days: number | null | undefined): string {
  if (days == null) return tMail('mail.mbox.unlimited');
  const hit = RETENTION_OPTIONS.find((o) => o.days === days);
  return hit ? tMail(hit.labelKey) : tMail('mail.mbox.r.days').replace('{n}', String(days));
}

function fmtBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n < 0) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

interface Row {
  key: string;
  /** 내가 만든 메일함이면 그 id. 시스템이면 null. */
  id: string | null;
  name: string;
  depth: number;
  hasChild: boolean;
  retentionDays: number | null;
  /** 보관기간을 못 고치는 줄(시스템). */
  retentionFixed: boolean;
  canEmpty: boolean;
  stat: MailboxStat | null;
  node: MailFolderNode | null;
}

/** 평평한 목록을 나무 순서로 편다(부모 바로 밑에 자식) — 서버는 순서를 안 접는다. */
function flatten(folders: MailFolderNode[]): Array<{ node: MailFolderNode; depth: number; hasChild: boolean }> {
  const byParent = new Map<string | null, MailFolderNode[]>();
  for (const f of folders) {
    const list = byParent.get(f.parentId);
    if (list) list.push(f); else byParent.set(f.parentId, [f]);
  }
  const out: Array<{ node: MailFolderNode; depth: number; hasChild: boolean }> = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const n of byParent.get(parentId) ?? []) {
      out.push({ node: n, depth, hasChild: (byParent.get(n.id)?.length ?? 0) > 0 });
      walk(n.id, depth + 1);
    }
  };
  walk(null, 1);
  return out;
}

/** ⋯ 메뉴 상자의 크기 — 좌표를 «미리» 재려면 알아야 한다. CSS(`.mbox__menu`)와 함께 고친다. */
const MENU_W = 170;
const MENU_H = 210;
/** 창 가장자리에서 이만큼은 띄운다. */
const MENU_EDGE = 8;
const MENU_GAP = 4;

/**
 * 누른 단추 자리에서 ⋯ 메뉴 좌표를 정한다.
 * 기본은 단추 오른쪽 끝에 맞춰 아래로. 아래가 모자라면 위로, 옆이 모자라면 안쪽으로 당긴다.
 */
function placeMenu(r: DOMRect): { top: number; left: number } {
  const left = Math.max(MENU_EDGE, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - MENU_EDGE));
  let top = r.bottom + MENU_GAP;
  if (top + MENU_H > window.innerHeight - MENU_EDGE) {
    top = Math.max(MENU_EDGE, r.top - MENU_H - MENU_GAP);
  }
  return { top, left };
}

/** 자기 자신과 자기 자손 — 「이동」 후보에서 뺀다(나무가 끊어진다). */
function descendantsOf(folders: MailFolderNode[], id: string): Set<string> {
  const out = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (f.parentId && out.has(f.parentId) && !out.has(f.id)) { out.add(f.id); grew = true; }
    }
  }
  return out;
}

type Ask =
  | { kind: 'delete'; node: MailFolderNode; kids: number }
  | { kind: 'empty'; node: MailFolderNode }
  | { kind: 'tag-delete'; tag: MailTag };

export function MailSettingsFolders() {
  const [folders, setFolders] = useState<MailFolderNode[]>([]);
  const [stats, setStats] = useState<MailboxStats | null>(null);
  const [tags, setTags] = useState<MailTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  const [menuFor, setMenuFor] = useState<string | null>(null);
  /**
   * ⋯ 메뉴의 좌표 (2026-09-06).
   *
   * ★★ 표 상자가 `overflow-x:auto` 라 그 안에 `absolute` 로 두면 **잘린다** —
   *    한 축이 `auto` 면 나머지 축의 `visible` 도 `auto` 로 계산되기 때문이다(CSS 규칙).
   *    실제로 「⋯ 를 누르면 스크롤만 생기고 팝업이 숨는다」는 신고가 이것이었다.
   *    그래서 body 로 띄우고(`position:fixed`) **누른 단추 자리를 재서** 좌표를 넣는다.
   */
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [folderModal, setFolderModal] = useState<{ mode: 'create' | 'move'; target: MailFolderNode | null } | null>(null);
  const [tagModal, setTagModal] = useState<{ target: MailTag | null } | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const [f, s, t] = await Promise.all([listMailFolders(), getMailboxStats(), listMailTags()]);
    setFolders(f);
    setStats(s);
    setTags(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void load()
      .catch(() => { if (!cancelled) setNotice(tMail('mail.settings.load_failed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  /**
   * 왼쪽 레일의 나무에서 메일함을 고치면 **이 표도 그 자리에서 따라간다**
   * (2026-09-06). 반대 방향은 아래 `changed()` 가 맡는다.
   * ★ `notifySelf` 는 내가 고쳤을 때 나에게 되돌아오는 것을 막는 손잡이다 —
   *   `run()` 이 이미 `load()` 를 부르고 있어, 안 걸러 내면 같은 요청이 두 번 나간다.
   */
  const notifySelf = useRef<() => void>(() => {});
  useEffect(() => {
    const fn = () => { void load().catch(() => { /* 실패는 다음 손짓이 알려 준다 */ }); };
    notifySelf.current = fn;
    return subscribeMailFolders(fn);
  }, [load]);

  /** 메일함을 바꿨다 — 레일에게 알린다. 태그는 레일에 없으니 부르지 않는다. */
  const changed = useCallback(() => { notifyMailFoldersChanged(notifySelf.current); }, []);

  const closeMenu = useCallback(() => { setMenuFor(null); setMenuPos(null); }, []);

  // ⋯ 메뉴는 바깥을 누르면 닫힌다. 스크롤·창 크기가 바뀌어도 닫는다 — 좌표가 어긋나기 때문이다.
  useEffect(() => {
    if (!menuFor) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) closeMenu();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu(); };
    const onMove = () => closeMenu();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [menuFor, closeMenu]);

  const customById = useMemo(() => new Map(stats?.custom.map((c) => [c.id, c]) ?? []), [stats]);

  const rows: Row[] = useMemo(() => {
    const sys: Row[] = SYSTEM_ROWS.map((r) => ({
      key: `sys:${r.key}`,
      id: null,
      name: tMail(r.labelKey),
      depth: 1,
      hasChild: false,
      // ★ 스팸함만 30일이다 — 그것은 서버가 정한 값이라 여기서 고를 수 없다.
      retentionDays: r.key === 'spam' ? SPAM_RETENTION_DAYS : null,
      retentionFixed: true,
      canEmpty: r.canEmpty,
      stat: stats?.system?.[r.key] ?? null,
      node: null,
    }));
    const mine: Row[] = flatten(folders).map(({ node, depth, hasChild }) => ({
      key: `my:${node.id}`,
      id: node.id,
      name: node.name,
      depth,
      hasChild,
      retentionDays: node.retentionDays ?? null,
      retentionFixed: false,
      canEmpty: true,
      stat: customById.get(node.id) ?? null,
      node,
    }));
    return [...sys, ...mine];
  }, [folders, stats, customById]);

  /* ── 하는 일 ─────────────────────────────────────────────── */

  /**
   * 하는 일 한 벌 — 서버가 거절하면 **한 자리에만** 적는다.
   *
   * ★ 팝업이 열려 있으면 팝업 안에, 아니면 화면 아래에. 두 곳에 같이 적으면 같은 문장이
   *   두 번 뜨고, 팝업을 닫아도 아래에 남아 「아직 안 됐나」로 읽힌다.
   */
  const run = useCallback(async (fn: () => Promise<void>, failKey: MailI18nKey, inModal = false) => {
    setBusy(true);
    setModalErr(null);
    setNotice(null);
    try {
      await fn();
      await load();
      return true;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      const msg = code === 'FOLDER_NAME_TAKEN' ? tMail('mail.mbox.err_taken')
        : code === 'FOLDER_TOO_DEEP' ? tMail('mail.mbox.err_deep')
          : code === 'TAG_NAME_TAKEN' ? tMail('mail.tag.err_taken')
            : status === 409 ? tMail('mail.mbox.err_taken')
              : tMail(failKey);
      if (inModal) setModalErr(msg); else setNotice(msg);
      return false;
    } finally {
      setBusy(false);
    }
  }, [load]);

  const setRetention = useCallback(async (row: Row, value: string) => {
    const days = RETENTION_OPTIONS.find((o) => o.value === value)?.days ?? null;
    if (!row.node) return;
    await run(async () => { await updateMailFolder(row.node!.id, { retentionDays: days }); }, 'mail.mbox.save_failed');
  }, [run]);

  /* ── 그리기 ─────────────────────────────────────────────── */

  const columns: ReadonlyArray<TableColumn<Row>> = [
    {
      key: 'name',
      header: tMail('mail.mbox.col.name'),
      rowHeader: true,
      render: (r) => (
        <span className="mbox__name" style={{ paddingLeft: `${(r.depth - 1) * 16}px` }}>
          {/* 삼각형 자리는 늘 갖는다 — 있고 없고에 따라 이름이 흔들리면 훑을 수가 없다(정본 E1-2). */}
          <span className="mbox__tw" aria-hidden>{r.hasChild ? '▾' : ''}</span>
          <span className="mbox__nm">{r.name}</span>
        </span>
      ),
    },
    {
      key: 'retention',
      header: tMail('mail.mbox.col.retention'),
      width: '150px',
      render: (r) => (r.retentionFixed ? (
        <span className="mbox__mut" title={tMail('mail.mbox.system_locked')}>{retentionLabel(r.retentionDays)}</span>
      ) : (
        <Select
          size="sm"
          value={RETENTION_OPTIONS.find((o) => o.days === r.retentionDays)?.value ?? 'none'}
          onChange={(v) => { void setRetention(r, v); }}
          options={RETENTION_OPTIONS.map((o) => ({ value: o.value, label: tMail(o.labelKey) }))}
          ariaLabel={tMail('mail.mbox.retention_aria').replace('{n}', r.name)}
          /* ★ 표 상자(`overflow-x:auto`) 안이라 — 안 띄우면 목록이 상자에 잘린다. */
          portal
        />
      )),
    },
    {
      key: 'count',
      header: tMail('mail.mbox.col.count'),
      numeric: true,
      width: '120px',
      render: (r) => (r.stat === null
        ? <span className="mbox__mut">—</span>
        : <span>{r.stat.unread === null ? '—' : r.stat.unread} / {r.stat.total}</span>),
    },
    {
      key: 'size',
      header: tMail('mail.mbox.col.size'),
      numeric: true,
      width: '96px',
      render: (r) => <span className={r.stat ? undefined : 'mbox__mut'}>{fmtBytes(r.stat?.sizeBytes)}</span>,
    },
    {
      key: 'manage',
      header: tMail('mail.mbox.col.manage'),
      width: '210px',
      render: (r) => (
        <span className="mbox__acts">
          {/* 백업 — 서버에 길이 없다. 감추지 않고 잠근다(정본 P3). */}
          <Button
            variant="secondary" size="sm" disabled
            title={tMail('mail.mbox.not_yet')}
            iconLeft={<Download size={13} aria-hidden />}
          >
            {tMail('mail.mbox.backup')}
          </Button>
          <Button
            variant="secondary" size="sm"
            disabled={!r.canEmpty || busy}
            title={r.canEmpty ? undefined : tMail('mail.mbox.system_locked')}
            onClick={() => { if (r.node) setAsk({ kind: 'empty', node: r.node }); }}
          >
            {tMail('mail.mbox.empty')}
          </Button>
          <span className="mbox__menuwrap">
            <Button
              variant="secondary" size="sm" iconOnly
              aria-haspopup="menu"
              aria-expanded={menuFor === r.key}
              aria-label={tMail('mail.mbox.manage_aria').replace('{n}', r.name)}
              onClick={(e) => {
                if (menuFor === r.key) { closeMenu(); return; }
                setMenuPos(placeMenu(e.currentTarget.getBoundingClientRect()));
                setMenuFor(r.key);
              }}
            >
              <MoreVertical size={14} aria-hidden />
            </Button>
            {menuFor === r.key && menuPos && createPortal((
              <div
                className="mbox__menu mbox__menu--fixed"
                role="menu"
                ref={menuRef}
                style={{ top: menuPos.top, left: menuPos.left }}
              >
                <button
                  type="button" role="menuitem"
                  disabled={r.node === null}
                  title={r.node === null ? tMail('mail.mbox.system_locked') : undefined}
                  onClick={() => { closeMenu(); setModalErr(null); setFolderModal({ mode: 'move', target: r.node }); }}
                >
                  <FolderInput size={14} aria-hidden />{tMail('mail.mbox.move')}
                </button>
                <button type="button" role="menuitem" disabled title={tMail('mail.mbox.not_yet')}>
                  <Share2 size={14} aria-hidden />{tMail('mail.mbox.share')}
                </button>
                <button type="button" role="menuitem" disabled title={tMail('mail.mbox.not_yet')}>
                  <Upload size={14} aria-hidden />{tMail('mail.mbox.upload')}
                </button>
                <div className="mbox__menusep" />
                <button
                  type="button" role="menuitem" className="is-danger"
                  disabled={r.node === null}
                  title={r.node === null ? tMail('mail.mbox.system_locked') : undefined}
                  onClick={() => {
                    closeMenu();
                    if (!r.node) return;
                    // 부모를 지우면 자식도 함께 사라진다 — 몇 개인지 세어 창이 그것을 적는다(정본 E1-2).
                    const kids = descendantsOf(folders, r.node.id).size - 1;
                    setAsk({ kind: 'delete', node: r.node, kids });
                  }}
                >
                  <Trash2 size={14} aria-hidden />{tMail('mail.mbox.delete')}
                </button>
              </div>
            ), document.body)}
          </span>
        </span>
      ),
    },
  ];

  const tagColumns: ReadonlyArray<TableColumn<MailTag>> = [
    {
      key: 'tag',
      header: tMail('mail.tag.col.tag'),
      rowHeader: true,
      render: (t) => <span className="mailtag" data-tagcolor={t.color}>{t.name}</span>,
    },
    {
      key: 'manage',
      header: tMail('mail.tag.col.manage'),
      width: '180px',
      render: (t) => (
        <span className="mbox__acts">
          <Button
            variant="secondary" size="sm" disabled={busy}
            iconLeft={<Edit3 size={13} aria-hidden />}
            onClick={() => { setModalErr(null); setTagModal({ target: t }); }}
          >
            {tMail('mail.tag.edit')}
          </Button>
          <Button
            variant="quiet-danger" size="sm" disabled={busy}
            onClick={() => setAsk({ kind: 'tag-delete', tag: t })}
          >
            {tMail('mail.tag.delete')}
          </Button>
        </span>
      ),
    },
  ];

  /**
   * 상위 후보 — 「이동」에서는 자기 자신·자기 자손을 뺀다(나무가 끊어진다).
   *
   * ★ **맨 위 「내 메일함」은 팝업이 붙인다**(`MBOX_ROOT`). 그래서 이 목록이 비어도
   *   고를 것이 0개가 되지 않는다 — 새 계정이 메일함을 못 만들던 이유가 그것이었다.
   * ★ **나무 차례로 편다.** 서버 차례(sortOrder)대로 두면 자식이 부모에서 떨어져 서서
   *   들여쓰기가 거짓말을 한다. 단은 「내 메일함」을 0 으로 세어 한 단씩 들어간다.
   */
  const parentOptions = useMemo(() => {
    const skip = folderModal?.mode === 'move' && folderModal.target
      ? descendantsOf(folders, folderModal.target.id)
      : new Set<string>();
    return flatten(folders)
      .filter(({ node }) => !skip.has(node.id))
      .map(({ node, depth }) => ({ id: node.id, label: `${'· '.repeat(depth)}${node.name}` }));
  }, [folders, folderModal]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  return (
    <div className="wc-set__body">
      <section className="wc-set__sec" aria-labelledby="mbox-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mbox-h">{tMail('mail.mbox.heading')}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setFolderModal({ mode: 'create', target: null }); }}
          >
            {tMail('mail.mbox.add')}
          </Button>
        </div>

        <Table
          caption={tMail('mail.mbox.heading')}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.key}
          rowClassName={(r) => (r.retentionFixed ? 'is-system' : undefined)}
          wrapClassName="mbox__tablewrap"
        />

        <p className="wc-set__hint">{tMail('mail.mbox.system_note')}</p>
        <p className="wc-set__hint">{tMail('mail.mbox.size_note')}</p>
        <p className="wc-set__hint">{tMail('mail.mbox.locked_note')}</p>
        {folders.length === 0 && <p className="wc-set__hint">{tMail('mail.mbox.none')}</p>}
      </section>

      <section className="wc-set__sec" aria-labelledby="mtag-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mtag-h">{tMail('mail.tag.heading')}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setTagModal({ target: null }); }}
          >
            {tMail('mail.tag.add')}
          </Button>
        </div>

        <Table
          caption={tMail('mail.tag.heading')}
          columns={tagColumns}
          rows={tags}
          rowKey={(t) => t.id}
          empty={<span>{tMail('mail.tag.none')}</span>}
          wrapClassName="mbox__tablewrap"
        />
        <p className="wc-set__hint">{tMail('mail.tag.not_attached')}</p>
      </section>

      {notice && <p className="wc-set__hint mailset__err" role="alert">{notice}</p>}

      {folderModal && (
        <MailFolderModal
          mode={folderModal.mode}
          target={folderModal.target}
          parents={parentOptions}
          busy={busy}
          error={modalErr}
          onClose={() => { setFolderModal(null); setModalErr(null); }}
          onSubmit={async ({ name, parentId }) => {
            const ok = await run(async () => {
              if (folderModal.mode === 'create') await createMailFolder(name, parentId);
              else if (folderModal.target) await updateMailFolder(folderModal.target.id, { parentId });
            }, folderModal.mode === 'create' ? 'mail.mbox.err_failed' : 'mail.mbox.save_failed', true);
            if (ok) { setFolderModal(null); changed(); }
          }}
        />
      )}

      {tagModal && (
        <MailTagModal
          target={tagModal.target}
          busy={busy}
          error={modalErr}
          onClose={() => { setTagModal(null); setModalErr(null); }}
          onSubmit={async ({ name, color }: { name: string; color: MailTagColor }) => {
            const ok = await run(async () => {
              if (tagModal.target) await updateMailTag(tagModal.target.id, { name, color });
              else await createMailTag(name, color);
            }, 'mail.tag.err_failed', true);
            if (ok) setTagModal(null);
          }}
        />
      )}

      {/* 되돌릴 수 없는 일은 반드시 한 번 묻는다 — 목록·상세가 쓰던 그 창이다. */}
      <MailConfirmDialog
        open={ask !== null}
        danger={ask?.kind !== 'empty'}
        busy={busy}
        title={
          ask?.kind === 'delete' ? tMail('mail.mbox.del_title').replace('{n}', ask.node.name)
            : ask?.kind === 'empty' ? tMail('mail.mbox.empty_title').replace('{n}', ask.node.name)
              : ask?.kind === 'tag-delete' ? tMail('mail.tag.del_title').replace('{n}', ask.tag.name)
                : ''
        }
        description={
          ask?.kind === 'delete'
            ? (ask.kids > 0
              ? tMail('mail.mbox.del_desc_kids').replace('{k}', String(ask.kids))
              : tMail('mail.mbox.del_desc'))
            : ask?.kind === 'empty' ? tMail('mail.mbox.empty_desc')
              : ask?.kind === 'tag-delete' ? tMail('mail.tag.del_desc')
                : ''
        }
        confirmLabel={ask?.kind === 'empty' ? tMail('mail.mbox.empty') : tMail('mail.mbox.delete')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          const cur = ask;
          if (!cur) return;
          void (async () => {
            const ok = await run(async () => {
              if (cur.kind === 'delete') await deleteMailFolder(cur.node.id);
              else if (cur.kind === 'empty') await emptyFolder('my', cur.node.id);
              else await deleteMailTag(cur.tag.id);
            }, cur.kind === 'tag-delete' ? 'mail.tag.err_failed' : 'mail.mbox.save_failed');
            if (ok) { setAsk(null); if (cur.kind !== 'tag-delete') changed(); }
          })();
        }}
      />
    </div>
  );
}
